import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

export const DarkModeToggle: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();

  return (
    <button
      onClick={toggleTheme}
      className={`
        relative inline-flex items-center justify-center
        w-14 h-8 rounded-full transition-all duration-300 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        ${isDark 
          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 shadow-lg shadow-indigo-500/25' 
          : 'bg-gradient-to-r from-yellow-400 to-orange-500 shadow-lg shadow-yellow-500/25'
        }
        hover:scale-105 active:scale-95
        group
      `}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      {/* Background glow effect */}
      <div className={`
        absolute inset-0 rounded-full blur-md transition-all duration-300
        ${isDark 
          ? 'bg-gradient-to-r from-indigo-600 to-purple-600 opacity-50' 
          : 'bg-gradient-to-r from-yellow-400 to-orange-500 opacity-50'
        }
        group-hover:opacity-75
      `} />
      
      {/* Toggle circle */}
      <div className={`
        relative z-10 flex items-center justify-center
        w-6 h-6 rounded-full transition-all duration-300 ease-in-out
        transform ${isDark ? 'translate-x-3' : '-translate-x-3'}
        ${isDark 
          ? 'bg-white shadow-lg' 
          : 'bg-white shadow-lg'
        }
        group-hover:scale-110
      `}>
        {/* Icon container with rotation animation */}
        <div className={`
          transition-all duration-500 ease-in-out
          ${isDark ? 'rotate-180' : 'rotate-0'}
        `}>
          {isDark ? (
            // Moon icon
            <svg 
              className="w-3 h-3 text-indigo-600" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path 
                fillRule="evenodd" 
                d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" 
                clipRule="evenodd" 
              />
            </svg>
          ) : (
            // Sun icon
            <svg 
              className="w-3 h-3 text-orange-500" 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path 
                fillRule="evenodd" 
                d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" 
                clipRule="evenodd" 
              />
            </svg>
          )}
        </div>
      </div>

      {/* Floating particles effect */}
      <div className={`
        absolute inset-0 rounded-full overflow-hidden
        ${isDark ? 'opacity-100' : 'opacity-0'}
        transition-opacity duration-300
      `}>
        {/* Stars */}
        <div className="absolute top-1 left-2 w-0.5 h-0.5 bg-white rounded-full animate-pulse" />
        <div className="absolute top-2 right-2 w-0.5 h-0.5 bg-white rounded-full animate-pulse delay-100" />
        <div className="absolute bottom-2 left-3 w-0.5 h-0.5 bg-white rounded-full animate-pulse delay-200" />
      </div>

      {/* Sun rays effect */}
      <div className={`
        absolute inset-0 rounded-full
        ${!isDark ? 'opacity-100' : 'opacity-0'}
        transition-opacity duration-300
      `}>
        {/* Animated rays */}
        <div className="absolute top-0 left-1/2 w-0.5 h-1 bg-white rounded-full transform -translate-x-1/2 animate-pulse" />
        <div className="absolute bottom-0 left-1/2 w-0.5 h-1 bg-white rounded-full transform -translate-x-1/2 animate-pulse delay-75" />
        <div className="absolute left-0 top-1/2 w-1 h-0.5 bg-white rounded-full transform -translate-y-1/2 animate-pulse delay-150" />
        <div className="absolute right-0 top-1/2 w-1 h-0.5 bg-white rounded-full transform -translate-y-1/2 animate-pulse delay-225" />
      </div>
    </button>
  );
};
